import React, { useState, useEffect } from "react";
import { CardBody, CardTitle, Button } from "reactstrap";
import Parametri from "../../parametri";
import * as XLSX from "xlsx"; // npm i xlsx --force
import Clessidra from "../Clessidra.js";

// Stati
const STATO_SELEZIONA = 1;
const STATO_RICERCA_DATI = 2;
const STATO_ERRORE_CONNESSIONE = 3;
const STATO_GENERA_XLSX = 4;
const STATO_FINE = 5;

function ReportCompetenzaAbbonamenti(props) {
  const [statoPagina, setStatoPagina] = useState(STATO_SELEZIONA);

  const oggiAAAAMMDD = new Date().toLocaleDateString("en-CA"); // AAAA-MM-DD
  const oggiAAAA = oggiAAAAMMDD.substring(0, 4);
  const primoAnno = 2025;
  const ultimoAnno = parseInt(oggiAAAA, 10);

  const [annoSelezionato, setAnnoSelezionato] = useState(oggiAAAA);
  const [datiReport, setDatiReport] = useState([]); // json con i dati del report
  const [datiSchema, setDatiSchema] = useState([]); // json con il tipo delle colonne del report
  const [flagAttesaGenerazioneFile, setFlagAttesaGenerazioneFile] = useState(false);

  useEffect(() => {
    if (statoPagina === STATO_RICERCA_DATI) ricercaDati();
    if (statoPagina === STATO_GENERA_XLSX) estraiExcel();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [statoPagina]);

  function ricercaDati() {
    const requestOptions = {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        op: "reportCompetenzaAbbonamenti",
        annoSelezionato: annoSelezionato,
        emak: props.emak,
        sessionId: props.sessionId,
      }),
    };

    fetch(Parametri("serverURL", props.identitaSelezionata), requestOptions)
      .then((risposta) => risposta.json())
      .then(
        (valoreDiRitorno) => {
          if (valoreDiRitorno.risultatoOperazione === 0) {
            window.alert(
              "Si è verificato un errore gestendo la richiesta, se l'errore persiste contattare l'assistenza tecnica"
            );
            setStatoPagina(STATO_SELEZIONA);
            return;
          }

          if (valoreDiRitorno.risultatoOperazione === 2) {
            props.setPagina(props.PAGINE.tornaLogin);
            return;
          }

          if (valoreDiRitorno.risultatoOperazione === 1) {
            // report può essere array, oppure qualcosa che “contiene” array
            const report = valoreDiRitorno.report || [];
            setDatiReport(Array.isArray(report) ? report : []);
            setDatiSchema(valoreDiRitorno.schema || {});
            setStatoPagina(STATO_GENERA_XLSX);
            return;
          }

          // caso inatteso
          setStatoPagina(STATO_SELEZIONA);
        },
        (error) => {
          setStatoPagina(STATO_ERRORE_CONNESSIONE);
        }
      );
  }

  // ---- XLSX helpers ----

  function displayValueForWidth(v, key) {
    const meta = datiSchema?.[key];
    if (!meta || v === null || v === undefined) return "";

    if (meta.type === "date") {
      // lunghezza visiva tipica dd/mm/yyyy
      return "dd/mm/yyyy";
    }

    if (meta.type === "money") {
      // lunghezza visiva tipica importo
      return "999.999,99";
    }

    return String(v);
  }

  // Converte "YYYY-MM-DD" in numero data Excel (serial)
  function excelDateFromYMD(ymd) {
    if (!ymd) return null;
    const s = String(ymd);

    if (!/^\d{4}-\d{2}-\d{2}$/.test(s)) return null;

    const y = parseInt(s.substring(0, 4), 10);
    const m = parseInt(s.substring(5, 7), 10);
    const d = parseInt(s.substring(8, 10), 10);

    // UTC per evitare problemi di timezone
    const dt = new Date(Date.UTC(y, m - 1, d));
    if (isNaN(dt.getTime())) return null;

    // Excel 1900 date system: epoch 1899-12-30
    const excelEpoch = Date.UTC(1899, 11, 30);
    return (dt.getTime() - excelEpoch) / (24 * 60 * 60 * 1000);
  }

  // Converte valore in qualcosa di “scrivibile in cella”, MA rispettando lo schema
  function toCellValue(v, key) {
    const meta = (datiSchema && datiSchema[key]) ? datiSchema[key] : null;
    const type = meta && meta.type ? meta.type : "";

    if (v === null || v === undefined) return "";

    // DATE: vogliamo un numero Excel vero
    if (type === "date") {
      // se già Date JS
      if (v instanceof Date) {
        const y = v.getUTCFullYear();
        const m = String(v.getUTCMonth() + 1).padStart(2, "0");
        const d = String(v.getUTCDate()).padStart(2, "0");
        const n = excelDateFromYMD(`${y}-${m}-${d}`);
        return n === null ? "" : n;
      }

      // se stringa "YYYY-MM-DD"
      const n = excelDateFromYMD(v);
      return n === null ? "" : n;
    }

    // MONEY/NUMBER: vogliamo un numero vero
    if (type === "money" || type === "number" || type === "integer") {
      if (typeof v === "number") return v;

      // prova a convertire stringhe numeriche
      // (se arrivano "12.34" ok; se arrivano "12,34" proviamo a sistemare)
      const s = String(v).trim();
      if (s === "") return "";

      let n = parseFloat(s);
      if (isNaN(n)) n = parseFloat(s.replace(",", "."));
      return isNaN(n) ? "" : n;
    }

    // BOOLEAN: lascia boolean (Excel lo gestisce), oppure testo se preferisci
    if (typeof v === "boolean") return v;

    // OGGETTI/ARRAY: stringify
    if (typeof v === "object") {
      try {
        return JSON.stringify(v);
      } catch (e) {
        return String(v);
      }
    }

    // DEFAULT: stringa
    if (typeof v === "number") return v;
    return String(v);
  }

  // Raccoglie tutte le colonne possibili da tutte le righe (anche se non uniformi)
  function collectAllKeys(rows) {
    const map = {};
    for (let i = 0; i < rows.length; i++) {
      const r = rows[i];
      if (!r || typeof r !== "object") continue;
      Object.keys(r).forEach((k) => (map[k] = true));
    }
    return Object.keys(map);
  }

  // Auto-width colonne (senza dipendenze esterne)
  function autoWidthFromAoA(aoa, keys, minW, maxW) {
    if (!aoa || !aoa.length) return [];

    const maxCols = keys.length;
    const widths = [];

    for (let c = 0; c < maxCols; c++) {
      // 1) parte dal testo dell’header (keys[c])
      let best = minW;
      const headerParts = String(keys[c] || "").split("\n");
      for (let i = 0; i < headerParts.length; i++) best = Math.max(best, headerParts[i].length);

      // 2) poi considera i valori delle righe (da r=1 in poi)
      for (let r = 1; r < aoa.length; r++) {
        const rawVal = aoa[r] && aoa[r][c] !== undefined ? aoa[r][c] : "";
        const key = keys[c];

        const displayVal = displayValueForWidth(rawVal, key);
        const s = displayVal ? String(displayVal) : "";

        const parts = s.split("\n");
        for (let i = 0; i < parts.length; i++) {
          best = Math.max(best, parts[i].length);
        }
      }

      best = Math.min(maxW, best);
      widths.push({ wch: best + 1 }); // +1 margine
    }

    return widths;
  }

  // Applica formati di visualizzazione Excel (z) in base a schema
  function applyFormats(worksheet, keys, rowsCount) {
    if (!datiSchema) return;

    for (let c = 0; c < keys.length; c++) {
      const key = keys[c];
      const meta = datiSchema[key];
      if (!meta || !meta.type) continue;

      for (let r = 1; r <= rowsCount; r++) { // r=0 è header
        const addr = XLSX.utils.encode_cell({ r, c });
        const cell = worksheet[addr];
        if (!cell) continue;

        if (meta.type === "date") {
          // se la cella contiene numero (come impostato da toCellValue) => format data
          if (typeof cell.v === "number") {
            cell.t = "n";
            cell.z = meta.format || "dd/mm/yyyy";
          }
        }

        if (meta.type === "money") {
          if (typeof cell.v === "number") {
            cell.t = "n";
            cell.z = meta.format || "#,##0.00";
          }
        }

        if (meta.type === "number") {
          if (typeof cell.v === "number") {
            cell.t = "n";
            cell.z = meta.format || "#,##0.00";
          }
        }

        if (meta.type === "integer") {
          if (typeof cell.v === "number") {
            cell.t = "n";
            cell.z = meta.format || "0";
          }
        }
      }
    }
  }

  async function estraiExcel() {
    if (flagAttesaGenerazioneFile) return;

    setFlagAttesaGenerazioneFile(true);

    try {
      const rows = Array.isArray(datiReport) ? datiReport : [];

      if (rows.length === 0) {
        window.alert("Nessun dato da esportare.");
        setStatoPagina(STATO_SELEZIONA);
        return;
      }

      // colonne dinamiche
      const keys = collectAllKeys(rows);

      // costruzione AOA: prima riga header, poi dati
      const aoa = [];
      aoa.push(keys);

      for (let i = 0; i < rows.length; i++) {
        const r = rows[i] || {};
        const line = [];
        for (let k = 0; k < keys.length; k++) {
          const key = keys[k];
          line.push(toCellValue(r[key], key));
        }
        aoa.push(line);
      }

      const worksheet = XLSX.utils.aoa_to_sheet(aoa);

      // freeze prima riga
      worksheet["!freeze"] = { xSplit: 0, ySplit: 1 };

      // formati (date/money) via schema
      applyFormats(worksheet, keys, rows.length);

      // auto-width
      worksheet["!cols"] = autoWidthFromAoA(aoa, keys, 8, 80);

      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, "Report");

      const oggi = new Date().toISOString().split("T")[0];
      const nomeFile = `${oggi} report abbonamenti ${annoSelezionato}.xlsx`;

      XLSX.writeFile(workbook, nomeFile);

      setStatoPagina(STATO_FINE);
    } catch (e) {
      window.alert("Errore durante la generazione del file XLSX.");
      setStatoPagina(STATO_SELEZIONA);
    } finally {
      setFlagAttesaGenerazioneFile(false);
    }
  }


  // temporanea attivazione solo per EMA18
  /*
  if (props.identitaSelezionata.nome_database !== "EMA18") {
    return (
      <div className="schedaSpazioCentrale" style={{ height: props.dimensioniApp.yMain }}>
        Funzione presto disponibile
      </div>
    );
  }
    */

  if (statoPagina === STATO_RICERCA_DATI || statoPagina === STATO_GENERA_XLSX) {
    return (
      <div className="schedaSpazioCentrale" style={{ height: props.dimensioniApp.yMain }}>
        Attendi la generazione del report...
        <Clessidra loading={true} />
      </div>
    );
  }

  if (statoPagina === STATO_ERRORE_CONNESSIONE) {
    return (
      <div className="schedaSpazioCentrale" style={{ height: props.dimensioniApp.yMain }}>
        Errore nella ricerca dei dati
      </div>
    );
  }

  if (statoPagina === STATO_SELEZIONA) {
    return (
      <div style={{ height: props.dimensioniApp.yMain, overflow: "hidden", display: "flex", flexDirection: "column" }}>
        <CardBody>
          <div>
            <CardTitle>Report Abbonamenti per Competenza</CardTitle>
            Report degli abbonamenti pagati nell'anno selezionato, con importi suddivisi nell'anno di competenza a seconda
            dell'intervallo di tempo coperto da ciascun abbonamento.
            <br />
            Questo report è utile al commercialista per poter attribuire gli incassi all'anno di competenza.
            <br />
            Il report viene scaricato in formato XLSX.
          </div>

          <div style={{ height: 20 }} />

          Estrai il report per gli abbonamenti che sono stati pagati nell'anno:
          <select
            className="inputStandard"
            value={annoSelezionato}
            onChange={(e) => setAnnoSelezionato(e.target.value)}
          >
            {Array.from({ length: ultimoAnno - primoAnno + 1 }, (_, i) => primoAnno + i).map((anno) => (
              <option key={anno} value={anno}>
                {anno}
              </option>
            ))}
          </select>

          <div style={{ height: 20 }} />

          <Button color="success" disabled={flagAttesaGenerazioneFile} onClick={() => setStatoPagina(STATO_RICERCA_DATI)}>
            Genera Report
          </Button>
        </CardBody>
      </div>
    );
  }

  if (statoPagina === STATO_FINE) {
    return (
      <div style={{ height: props.dimensioniApp.yMain, overflow: "hidden", display: "flex", flexDirection: "column" }}>
        <CardBody>
          <div>
            <CardTitle>Report Abbonamenti per Competenza</CardTitle>
            Report degli abbonamenti pagati nell'anno selezionato, con importi suddivisi nell'anno di competenza a seconda
            dell'intervallo di tempo coperto da ciascun abbonamento.
            <br />
            Questo report è utile al commercialista per poter attribuire gli incassi all'anno di competenza.
            <br />
            Il report viene scaricato in formato XLSX.
          </div>

          <div style={{ height: 20 }} />

          <b>Report generato</b>

          <div style={{ height: 20 }} />

          <Button color="success" disabled={flagAttesaGenerazioneFile} onClick={() => setStatoPagina(STATO_SELEZIONA)}>
            OK
          </Button>
        </CardBody>
      </div>
    );
  }  

  return (
    <div className="schedaSpazioCentrale" style={{ height: props.dimensioniApp.yMain }}>
      ReportCompetenzaAbbonamenti: Pagina non trovata ({statoPagina})
    </div>
  );
}

export default ReportCompetenzaAbbonamenti;
